import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, BellRing, Gift, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type ExitReasonRow = { id: string; label: string };

type AccountDeletionRetentionPayload = {
  enabled: boolean;
  windowDays: number;
  maxOffersPerWindow: number;
  offerTitle: string;
  offerBody: string;
  promoCode: string;
  retentionDiscountPercent: number;
  emailOfferCooldownDays: number;
  exitReasons: ExitReasonRow[];
};

type CustomerInactivityPolicyPayload = {
  enabled: boolean;
  inactivityDays: number;
  warningCount: number;
  warningIntervalDays: number;
  adminNotificationEmails: string[];
  deleteRequiresAdminApproval: boolean;
};

type ArchiveRow = {
  id: string;
  emailNormalized: string;
  formerUserId: string;
  deletedAt: string;
  profileName: string | null;
  orderCountSnapshot: number;
};

function formatRetentionDiscountInput(p: number): string {
  const n = Number(p);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 100) / 100;
  if (rounded % 1 === 0) return String(Math.round(rounded));
  return String(rounded);
}

export default function WorkspaceAccountDeletionSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const [model, setModel] = React.useState<AccountDeletionRetentionPayload>({
    enabled: true,
    windowDays: 90,
    maxOffersPerWindow: 2,
    offerTitle: '',
    offerBody: '',
    promoCode: '',
    retentionDiscountPercent: 10,
    emailOfferCooldownDays: 365,
    exitReasons: [],
  });
  const [busy, setBusy] = React.useState(true);
  const [inactivityBusy, setInactivityBusy] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savingInactivity, setSavingInactivity] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  /** String state so the field can be cleared while typing (controlled number always snapped "" → 0). */
  const [retentionDiscountDraft, setRetentionDiscountDraft] = React.useState(() =>
    formatRetentionDiscountInput(10),
  );
  const [inactivityPolicy, setInactivityPolicy] = React.useState<CustomerInactivityPolicyPayload>({
    enabled: false,
    inactivityDays: 180,
    warningCount: 2,
    warningIntervalDays: 7,
    adminNotificationEmails: [],
    deleteRequiresAdminApproval: true,
  });
  const [adminEmailsDraft, setAdminEmailsDraft] = React.useState('');
  const [inactivityDaysDraft, setInactivityDaysDraft] = React.useState('180');
  const [warningCountDraft, setWarningCountDraft] = React.useState('2');
  const [warningIntervalDaysDraft, setWarningIntervalDaysDraft] = React.useState('7');
  const [activeTab, setActiveTab] = React.useState<'retention' | 'inactivity' | 'archives'>(
    'retention',
  );
  const [archivesLoading, setArchivesLoading] = React.useState(false);
  const [archives, setArchives] = React.useState<ArchiveRow[]>([]);

  const load = React.useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/settings/account-deletion-retention`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as AccountDeletionRetentionPayload;
      setModel(data);
      setRetentionDiscountDraft(formatRetentionDiscountInput(data.retentionDiscountPercent));
    } catch {
      setError(
        t(
          'backoffice.account_deletion_retention_load_error',
          'Unable to load account deletion retention settings.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }, [authorizedFetch, t]);

  const loadInactivityPolicy = React.useCallback(async () => {
    setInactivityBusy(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/settings/customer-inactivity-policy`);
      if (!res.ok) throw new Error('load inactivity policy');
      const data = (await res.json()) as CustomerInactivityPolicyPayload;
      setInactivityPolicy(data);
      setAdminEmailsDraft((data.adminNotificationEmails ?? []).join(', '));
      setInactivityDaysDraft(String(data.inactivityDays ?? 180));
      setWarningCountDraft(String(data.warningCount ?? 2));
      setWarningIntervalDaysDraft(String(data.warningIntervalDays ?? 7));
    } catch {
      setError(
        t(
          'backoffice.customer_inactivity_policy_load_error',
          'Unable to load customer inactivity policy settings.',
        ),
      );
    } finally {
      setInactivityBusy(false);
    }
  }, [authorizedFetch, t]);

  const parseIntegerDraft = React.useCallback(
    (raw: string, fallback: number, min: number, max: number): number => {
      const trimmed = raw.trim();
      if (trimmed === '') return fallback;
      const n = Number.parseInt(trimmed, 10);
      if (Number.isNaN(n)) return fallback;
      return Math.min(max, Math.max(min, n));
    },
    [],
  );

  React.useEffect(() => {
    void load();
    void loadInactivityPolicy();
  }, [load, loadInactivityPolicy]);

  React.useEffect(() => {
    if (activeTab !== 'archives') return;
    let cancelled = false;
    void (async () => {
      setArchivesLoading(true);
      try {
        const res = await authorizedFetch(
          `${API_BASE}/auth/workspace/customers/deletion-archives?page=1&pageSize=20`,
        );
        if (!res.ok) throw new Error('load archives');
        const data = (await res.json()) as { items: ArchiveRow[] };
        if (!cancelled) setArchives(data.items ?? []);
      } catch {
        if (!cancelled) setArchives([]);
      } finally {
        if (!cancelled) setArchivesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, authorizedFetch]);

  const parseRetentionDiscountPercent = React.useCallback((raw: string): number => {
    const trimmed = raw.trim();
    if (trimmed === '') return 0;
    const n = Number.parseFloat(trimmed);
    if (Number.isNaN(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const retentionDiscountPercent = parseRetentionDiscountPercent(retentionDiscountDraft);
      const res = await authorizedFetch(`${API_BASE}/settings/account-deletion-retention`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...model, retentionDiscountPercent }),
      });
      if (!res.ok) throw new Error('save');
      const data = (await res.json()) as AccountDeletionRetentionPayload;
      setModel(data);
      setRetentionDiscountDraft(formatRetentionDiscountInput(data.retentionDiscountPercent));
      setSuccess(
        t(
          'backoffice.account_deletion_retention_saved',
          'Account deletion retention settings saved.',
        ),
      );
    } catch {
      setError(
        t(
          'backoffice.account_deletion_retention_save_error',
          'Unable to save settings.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const saveInactivityPolicy = async () => {
    setSavingInactivity(true);
    setError(null);
    setSuccess(null);
    try {
      const next: CustomerInactivityPolicyPayload = {
        ...inactivityPolicy,
        inactivityDays: parseIntegerDraft(inactivityDaysDraft, 180, 1, 3650),
        warningCount: parseIntegerDraft(warningCountDraft, 2, 0, 10),
        warningIntervalDays: parseIntegerDraft(warningIntervalDaysDraft, 7, 1, 365),
        adminNotificationEmails: adminEmailsDraft
          .split(',')
          .map((x) => x.trim().toLowerCase())
          .filter(Boolean),
      };
      const res = await authorizedFetch(`${API_BASE}/settings/customer-inactivity-policy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('save inactivity policy');
      const data = (await res.json()) as CustomerInactivityPolicyPayload;
      setInactivityPolicy(data);
      setAdminEmailsDraft((data.adminNotificationEmails ?? []).join(', '));
      setInactivityDaysDraft(String(data.inactivityDays ?? 180));
      setWarningCountDraft(String(data.warningCount ?? 2));
      setWarningIntervalDaysDraft(String(data.warningIntervalDays ?? 7));
      setSuccess(
        t(
          'backoffice.customer_inactivity_policy_saved',
          'Customer inactivity lifecycle policy saved.',
        ),
      );
    } catch {
      setError(
        t(
          'backoffice.customer_inactivity_policy_save_error',
          'Unable to save customer inactivity policy settings.',
        ),
      );
    } finally {
      setSavingInactivity(false);
    }
  };

  const updateExitReason = (index: number, patch: Partial<ExitReasonRow>) => {
    setModel((prev) => {
      const next = [...prev.exitReasons];
      next[index] = { ...next[index], ...patch };
      return { ...prev, exitReasons: next };
    });
  };

  const removeExitReason = (index: number) => {
    setModel((prev) => ({
      ...prev,
      exitReasons: prev.exitReasons.filter((_, i) => i !== index),
    }));
  };

  const addExitReason = () => {
    setModel((prev) => ({
      ...prev,
      exitReasons: [...prev.exitReasons, { id: '', label: '' }],
    }));
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {t('backoffice.sidebar_account_deletion_retention', 'Account Deletion')}
          </h1>
          <p className="mt-1 max-w-4xl workspace-page-subtitle text-slate-600">
            Manage customer account lifecycle controls from one place: retention offer behavior,
            inactivity cleanup automation, and deleted account archives.
          </p>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              {[
                {
                  id: 'retention' as const,
                  label: 'Retention Offer',
                  icon: <Gift className="h-4 w-4" />,
                },
                {
                  id: 'inactivity' as const,
                  label: 'Inactivity Lifecycle',
                  icon: <BellRing className="h-4 w-4" />,
                },
                {
                  id: 'archives' as const,
                  label: 'Deleted Accounts Archive',
                  icon: <Archive className="h-4 w-4" />,
                },
              ].map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={[
                      'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition',
                      active
                        ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
                    ].join(' ')}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-4 p-4 sm:p-5">
            {error ? (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            ) : null}
            {success ? (
              <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {success}
              </p>
            ) : null}

            {activeTab === 'retention' ? (
              busy ? (
                <p className="text-sm text-slate-600">{t('common.loading')}</p>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-3 text-sm text-indigo-900">
                    Configure the offer shown when a customer requests account deletion.
                  </div>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm">
                    <span className="font-semibold text-slate-900">
                      {t('backoffice.account_deletion_retention_enabled', 'Enable retention flow')}
                    </span>
                    <input
                      type="checkbox"
                      checked={model.enabled}
                      onChange={(e) => setModel((prev) => ({ ...prev, enabled: e.target.checked }))}
                    />
                  </label>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <label className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                      <span className="block font-semibold text-slate-900">
                        {t('backoffice.account_deletion_retention_window_days', 'Rolling window (days)')}
                      </span>
                      <span className="mt-1 block text-xs text-slate-600">
                        {t(
                          'backoffice.account_deletion_retention_window_days_hint',
                          'Previews in this window count toward the offer limit.',
                        )}
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={730}
                        step={1}
                        className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                        value={model.windowDays}
                        onChange={(e) =>
                          setModel((prev) => ({
                            ...prev,
                            windowDays: Math.min(730, Math.max(1, Number(e.target.value) || 1)),
                          }))
                        }
                      />
                    </label>
                    <label className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                      <span className="block font-semibold text-slate-900">
                        {t(
                          'backoffice.account_deletion_retention_max_offers',
                          'Max retention offers per window',
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-slate-600">
                        {t(
                          'backoffice.account_deletion_retention_max_offers_hint',
                          'After this many previews in the window, the retention offer is hidden and the exit survey is required.',
                        )}
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={50}
                        step={1}
                        className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                        value={model.maxOffersPerWindow}
                        onChange={(e) =>
                          setModel((prev) => ({
                            ...prev,
                            maxOffersPerWindow: Math.min(50, Math.max(0, Number(e.target.value) || 0)),
                          }))
                        }
                      />
                    </label>
                  </div>

                  <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="block font-semibold text-slate-900">
                      {t(
                        'backoffice.account_deletion_retention_email_cooldown_days',
                        'Per-email offer cooldown (days)',
                      )}
                    </span>
                    <span className="mt-1 block text-xs text-slate-600">
                      {t(
                        'backoffice.account_deletion_retention_email_cooldown_days_hint',
                        'After this email accepts the stay offer or uses the one-time discount, the retention offer is blocked for this many days (even if they delete the account and register again). Use 0 to disable.',
                      )}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={1825}
                      step={1}
                      className="mt-2 w-full max-w-[220px] rounded-lg border border-slate-200 px-3 py-2"
                      value={model.emailOfferCooldownDays}
                      onChange={(e) =>
                        setModel((prev) => ({
                          ...prev,
                          emailOfferCooldownDays: Math.min(
                            1825,
                            Math.max(0, Number(e.target.value) || 0),
                          ),
                        }))
                      }
                    />
                  </label>

                  <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="font-semibold text-slate-900">
                      {t('backoffice.account_deletion_retention_offer_title', 'Offer title')}
                    </span>
                    <input
                      type="text"
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                      value={model.offerTitle}
                      maxLength={200}
                      onChange={(e) => setModel((prev) => ({ ...prev, offerTitle: e.target.value }))}
                    />
                  </label>

                  <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="font-semibold text-slate-900">
                      {t('backoffice.account_deletion_retention_offer_body', 'Offer body')}
                    </span>
                    <textarea
                      className="mt-2 min-h-[120px] w-full rounded-lg border border-slate-200 px-3 py-2"
                      value={model.offerBody}
                      maxLength={4000}
                      onChange={(e) => setModel((prev) => ({ ...prev, offerBody: e.target.value }))}
                    />
                  </label>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.account_deletion_retention_promo_code', 'Optional promo code')}
                      </span>
                      <span className="mt-1 block text-xs text-slate-600">
                        {t(
                          'backoffice.account_deletion_retention_promo_hint',
                          'Shown to the customer; must exist in Promotions if you want it to apply automatically.',
                        )}
                      </span>
                      <input
                        type="text"
                        className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 uppercase"
                        value={model.promoCode}
                        maxLength={40}
                        onChange={(e) =>
                          setModel((prev) => ({
                            ...prev,
                            promoCode: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                    </label>

                    <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                      <span className="font-semibold text-slate-900">
                        {t(
                          'backoffice.account_deletion_retention_discount_percent',
                          'One-time stay discount (% of subtotal)',
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-slate-600">
                        {t(
                          'backoffice.account_deletion_retention_discount_percent_hint',
                          'Applied once on the customer’s next order after they accept the stay offer (shown on the order for finance).',
                        )}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                        value={retentionDiscountDraft}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            setRetentionDiscountDraft('');
                            return;
                          }
                          if (!/^\d{0,3}(\.\d{0,2})?$/.test(v)) return;
                          const n = Number.parseFloat(v);
                          if (Number.isNaN(n)) {
                            if (v === '.' || /\d+\.$/.test(v)) setRetentionDiscountDraft(v);
                            return;
                          }
                          if (n >= 0 && n <= 100) setRetentionDiscountDraft(v);
                        }}
                        onBlur={() => {
                          const p = parseRetentionDiscountPercent(retentionDiscountDraft);
                          setModel((prev) => ({ ...prev, retentionDiscountPercent: p }));
                          setRetentionDiscountDraft(formatRetentionDiscountInput(p));
                        }}
                      />
                    </label>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/30 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.account_deletion_retention_exit_reasons', 'Exit survey reasons')}
                      </span>
                      <button
                        type="button"
                        onClick={addExitReason}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t('backoffice.account_deletion_retention_add_reason', 'Add reason')}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">
                      {t(
                        'backoffice.account_deletion_retention_exit_reasons_hint',
                        'When the offer cap is exceeded, the customer must pick from these before deleting.',
                      )}
                    </p>
                    <div className="mt-3 space-y-2">
                      {model.exitReasons.map((row, i) => (
                        <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-slate-100 bg-slate-50/80 p-2">
                          <label className="min-w-[120px] flex-1 text-xs">
                            <span className="font-semibold text-slate-800">ID</span>
                            <input
                              type="text"
                              className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                              value={row.id}
                              maxLength={64}
                              onChange={(e) => updateExitReason(i, { id: e.target.value })}
                            />
                          </label>
                          <label className="min-w-[180px] flex-[2] text-xs">
                            <span className="font-semibold text-slate-800">
                              {t('backoffice.account_deletion_retention_label', 'Label')}
                            </span>
                            <input
                              type="text"
                              className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                              value={row.label}
                              maxLength={200}
                              onChange={(e) => updateExitReason(i, { label: e.target.value })}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeExitReason(i)}
                            className="rounded-lg border border-red-200 p-2 text-red-700 hover:bg-red-50"
                            aria-label={t('common.remove')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end border-t border-slate-200 pt-3">
                    <button
                      type="button"
                      onClick={() => void save()}
                      disabled={saving}
                      className="rounded-xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {saving ? t('common.saving') : t('common.save')}
                    </button>
                  </div>
                </div>
              )
            ) : activeTab === 'inactivity' ? (
              inactivityBusy ? (
              <p className="text-sm text-slate-600">{t('common.loading')}</p>
              ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3 text-sm text-sky-900">
                  Configure warning cadence, account blocking timing, and admin review routing.
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900">
                  <span className="inline-flex items-center gap-1 font-semibold">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Safety rule
                  </span>
                  <p className="mt-1">
                    Deletion requires admin approval. Inactivity automation only moves accounts to pending review.
                  </p>
                </div>
                <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm">
                  <span className="font-semibold text-slate-900">Enable inactivity lifecycle</span>
                  <input
                    type="checkbox"
                    checked={inactivityPolicy.enabled}
                    onChange={(e) =>
                      setInactivityPolicy((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                  />
                </label>

                <div className="grid gap-4 sm:grid-cols-3">
                  <label className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="block font-semibold text-slate-900">Inactivity threshold (days)</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                      value={inactivityDaysDraft}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d+$/.test(v)) setInactivityDaysDraft(v);
                      }}
                      onBlur={() => {
                        const parsed = parseIntegerDraft(inactivityDaysDraft, 180, 1, 3650);
                        setInactivityDaysDraft(String(parsed));
                        setInactivityPolicy((prev) => ({ ...prev, inactivityDays: parsed }));
                      }}
                    />
                  </label>
                  <label className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="block font-semibold text-slate-900">Warning emails before block</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                      value={warningCountDraft}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d+$/.test(v)) setWarningCountDraft(v);
                      }}
                      onBlur={() => {
                        const parsed = parseIntegerDraft(warningCountDraft, 2, 0, 10);
                        setWarningCountDraft(String(parsed));
                        setInactivityPolicy((prev) => ({ ...prev, warningCount: parsed }));
                      }}
                    />
                  </label>
                  <label className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                    <span className="block font-semibold text-slate-900">Warning interval (days)</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                      value={warningIntervalDaysDraft}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d+$/.test(v)) setWarningIntervalDaysDraft(v);
                      }}
                      onBlur={() => {
                        const parsed = parseIntegerDraft(warningIntervalDaysDraft, 7, 1, 365);
                        setWarningIntervalDaysDraft(String(parsed));
                        setInactivityPolicy((prev) => ({ ...prev, warningIntervalDays: parsed }));
                      }}
                    />
                  </label>
                </div>

                <label className="block rounded-xl border border-slate-200 bg-slate-50/40 p-3 text-sm">
                  <span className="font-semibold text-slate-900">Admin notification emails</span>
                  <span className="mt-1 block text-xs text-slate-600">
                    Comma-separated list. Leave empty to notify active admin users automatically.
                  </span>
                  <textarea
                    className="mt-2 min-h-[88px] w-full rounded-lg border border-slate-200 px-3 py-2"
                    value={adminEmailsDraft}
                    onChange={(e) => setAdminEmailsDraft(e.target.value)}
                  />
                </label>

                <div className="flex justify-end border-t border-slate-200 pt-3">
                  <button
                    type="button"
                    onClick={() => void saveInactivityPolicy()}
                    disabled={savingInactivity}
                    className="rounded-xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {savingInactivity ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
              )
            ) : archivesLoading ? (
              <p className="text-sm text-slate-600">{t('common.loading')}</p>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-sm text-amber-900">
                  Review recent deleted customer account snapshots and open details.
                </div>
                {!archives.length ? (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
                    No archive yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Deleted at</th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Name</th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Email</th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Orders</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {archives.map((row) => (
                          <tr key={row.id} className="hover:bg-slate-50/80">
                            <td className="whitespace-nowrap px-4 py-3 text-slate-800">
                              {new Date(row.deletedAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-slate-800">{row.profileName ?? '—'}</td>
                            <td className="max-w-[220px] truncate px-4 py-3 font-mono text-xs text-slate-700">
                              {row.emailNormalized}
                            </td>
                            <td className="px-4 py-3 text-slate-800">{row.orderCountSnapshot}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right">
                              <Link
                                to={`/workspace/customers/deletion-archives/${row.id}`}
                                className="font-medium text-brand-600 hover:text-brand-700"
                              >
                                Open
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-end border-t border-slate-200 pt-3">
                  <Link
                    to="/workspace/customers/deletion-archives"
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Open full archive page
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </MainLayout>
  );
}
